import cs from "classnames";
import React from "react";

import { createAsyncInput } from "src/common/components/AsyncInput";
import { useTrackFocusState } from "src/common/hooks/useTrackFocusState";

import "./Input.css";

const noop = (): void => {};

type InputProps = Omit<React.HTMLProps<HTMLInputElement>, "onChange"> & {
	prefix?: React.ReactNode;
	decorator?: React.ReactNode;
	value?: string | undefined;
	onChangeValue?(value: string | undefined): unknown;
};

export const SyncInput: React.FunctionComponent<InputProps> = ({
	className,
	type,
	disabled,
	prefix,
	decorator,
	value,
	onChangeValue = noop,
	onFocus: onFocusOriginal = noop,
	onBlur: onBlurOriginal = noop,
	...props
}) => {
	const [isFocused, onFocusTrack, onBlurTrack] = useTrackFocusState();
	const ref = React.useRef<HTMLInputElement | null>(null);

	React.useEffect(() => {
		const { current: el } = ref;
		if (el === null) {
			return;
		}
		const handler = (event: MouseEvent): void => {
			if (event.target instanceof HTMLInputElement) {
				event.target.blur();
			}
		};
		el.addEventListener("wheel", handler, { passive: false });
		return () => {
			el.removeEventListener("wheel", handler);
		};
	}, []);

	const onFocus = React.useCallback(
		(event: React.FocusEvent<HTMLInputElement, Element>) => {
			onFocusTrack();
			onFocusOriginal(event);
		},
		[onFocusOriginal, onFocusTrack]
	);

	const onBlur = React.useCallback(
		(event: React.FocusEvent<HTMLInputElement, Element>) => {
			onBlurTrack();
			onBlurOriginal(event);
		},
		[onBlurOriginal, onBlurTrack]
	);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChangeValue(event.target.value || undefined);
		},
		[onChangeValue]
	);

	return (
		<div
			className={cs("input", `input--${type}`, className, {
				"input--disabled": disabled,
				"input--focused": isFocused
			})}
		>
			{!!prefix && <div className="input__prefix">{prefix}</div>}
			<input
				{...props}
				ref={ref}
				type={type}
				disabled={disabled}
				value={value || ""}
				onFocus={onFocus}
				onBlur={onBlur}
				onChange={onChange}
			/>
			{!!decorator && <div className="input__decorator">{decorator}</div>}
		</div>
	);
};

export const Input = createAsyncInput(SyncInput);
